import React from 'react';
import { Button } from '../ui/button';
import { CircleCheck, Loader2, Send, Lock } from 'lucide-react';
import { money } from './aiqMeta';

const sendHint = (preview) => {
  if (preview.already_sent) return 'This order is already in AccountsIQ.';
  if (!preview.user_can_send) return 'Only admins and accountants can send invoices to AccountsIQ.';
  if (!preview.can_send) return 'Fix the items above, then send.';
  return 'Sends exactly what you see as a draft — you post it in AccountsIQ.';
};

export const InvoicePreviewFooter = ({ preview, sending, disabled, onSend, onClose }) => {
  const allowed = preview.can_send && preview.user_can_send;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
      <p className={`text-xs flex items-center gap-1.5 ${allowed ? 'text-zinc-500' : 'text-amber-800'}`} data-testid="aiq-preview-send-hint">
        {!preview.user_can_send && <Lock className="w-3.5 h-3.5" />}{sendHint(preview)}
      </p>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} data-testid="aiq-preview-close">Close</Button>
        <Button onClick={onSend} disabled={!allowed || disabled} className="bg-[#005F73] hover:bg-[#0A9396]" data-testid="aiq-preview-send-btn">
          {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
          {sending ? 'Sending…' : `Send draft · ${money(preview.totals?.gross, preview.totals?.currency)}`}
        </Button>
      </div>
    </div>
  );
};

const Row = ({ label, children, testId }) => (
  <div className="flex justify-between gap-6 py-1.5 border-b border-emerald-100 last:border-0 text-sm">
    <span className="text-zinc-500">{label}</span><span className="font-medium text-zinc-900 text-right" data-testid={testId}>{children}</span>
  </div>
);

export const InvoiceSentPanel = ({ result, dispatchNumber, onDone }) => (
  <div className="py-6 flex flex-col items-center text-center" data-testid="aiq-sent-panel">
    <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4 animate-in zoom-in-50 duration-300">
      <CircleCheck className="w-9 h-9 text-emerald-600" />
    </div>
    <h3 className="text-xl font-semibold text-zinc-900" data-testid="aiq-sent-message">{result.message}</h3>
    <p className="text-sm text-zinc-500 mt-1">Open AccountsIQ to check and post it.</p>
    <div className="w-full max-w-md mt-6 rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-2 text-left">
      <Row label="AccountsIQ draft" testId="aiq-sent-invoice-id"><span className="font-mono">#{result.invoice_id}</span></Row>
      <Row label="Customer">{result.customer?.name} <span className="font-mono text-xs text-zinc-500">({result.customer?.code})</span></Row>
      <Row label="Reference"><span className="font-mono">{result.external_reference || dispatchNumber}</span></Row>
      <Row label="Lines">{result.line_count}</Row>
      <Row label="Invoice total" testId="aiq-sent-total">{money(result.totals?.gross, result.totals?.currency)}</Row>
    </div>
    <Button className="mt-6 min-w-[140px] bg-[#005F73] hover:bg-[#0A9396]" onClick={onDone} data-testid="aiq-sent-done">Done</Button>
  </div>
);
